import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

interface AuthState {
  token: string | null;
  username: string | null;
  role: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<string | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthState>({
  token: null, username: null, role: null,
  isAuthenticated: false, isAdmin: false, loading: true,
  login: async () => null, logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

function decodePayload(token: string): { sub?: string; exp?: number; role?: string } | null {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount, restore + validate token from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('auth_token');
    if (stored) {
      const payload = decodePayload(stored);
      if (payload?.exp && payload.exp * 1000 > Date.now()) {
        setToken(stored);
        setUsername(payload.sub || null);
        setRole(payload.role || 'user');
      } else {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_username');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (user: string, password: string): Promise<string | null> => {
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/login`, { username: user, password });
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_username', data.username);
        setToken(data.token);
        setUsername(data.username);
        setRole(data.role || 'user');
        return null;
      }
      return 'Unexpected response';
    } catch (err: any) {
      return err?.response?.data?.detail || 'Login failed';
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_username');
    setToken(null);
    setUsername(null);
    setRole(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      token, username, role,
      isAuthenticated: !!token,
      isAdmin: role === 'admin',
      loading, login, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
